import { useState, useMemo } from 'react';
import { 
  Calendar as CalendarIcon, 
  MapPin, 
  Truck, 
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Home,
  Route,
  Clock,
  Info
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';

interface Trip {
  id: string;
  company: string;
  date: string; // Settlement/completion date
  startDate?: string; // Estimated trip start
  origin: string;
  destination: string;
  miles: number;
  amount: number;
  controlNumber: string;
  daysOnRoad: number;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Company colors
const COMPANY_COLORS: Record<string, string> = {
  'Bennett': 'blue',
  'Auto Driveaway': 'purple',
  'Quality Driveaway': 'green',
  'Coldiron': 'orange',
  'Dynamic': 'cyan',
  'Horizon': 'pink',
  'Lakefront': 'teal',
  'GPA': 'indigo',
  'default': 'gray'
};

// Estimate trip duration based on miles (average 500 miles/day for driveaway)
const estimateTripDays = (miles: number): number => {
  if (miles <= 0) return 1;
  return Math.max(1, Math.ceil(miles / 500));
};

// Per diem rates by fiscal year (M&IE rate - what truckers can claim)
// Truckers can deduct 80% of this amount (DOT special rule)
const PER_DIEM_RATES: Record<number, { mie: number; lodging: number }> = {
  2020: { mie: 66, lodging: 96 },
  2021: { mie: 66, lodging: 96 },
  2022: { mie: 59, lodging: 98 },
  2023: { mie: 59, lodging: 98 },
  2024: { mie: 59, lodging: 107 },
  2025: { mie: 68, lodging: 110 }, // FY 2025 (Oct 2024 - Sep 2025)
  2026: { mie: 68, lodging: 110 }, // FY 2026 (unchanged)
};

// Get per diem rate for a given year (M&IE only, which truckers claim)
const getPerDiemRate = (year: number): number => {
  return PER_DIEM_RATES[year]?.mie || 68; // Default to 2025 rate
};

export default function TripCalendar() {
  const { expenses } = useExpenses();
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');

  // Extract trips from settlements
  const trips = useMemo(() => {
    const settlementExpenses = expenses.filter(e => e.category === 'settlements');
    
    return settlementExpenses.map(exp => {
      // Use stored data first, fall back to parsing/estimation
      let origin = exp.origin || '';
      let destination = exp.destination || '';
      let miles = exp.miles || 0;
      let company = exp.company || 'Unknown';
      let controlNumber = exp.freightBillNumber || '';
      
      // If no stored origin/destination, try to parse from description
      if (!origin || !destination) {
        const locMatch = exp.description?.match(/([A-Z][a-z]+,?\s*[A-Z]{2})\s*(?:to|->|→|-)\s*([A-Z][a-z]+,?\s*[A-Z]{2})/);
        if (locMatch) {
          origin = origin || locMatch[1];
          destination = destination || locMatch[2];
        }
      }
      
      // If no stored company, try to detect from description
      if (company === 'Unknown') {
        const desc = (exp.description || '').toLowerCase();
        if (desc.includes('bennett')) company = 'Bennett';
        else if (desc.includes('auto driveaway')) company = 'Auto Driveaway';
        else if (desc.includes('quality')) company = 'Quality Driveaway';
        else if (desc.includes('coldiron')) company = 'Coldiron';
        else if (desc.includes('dynamic')) company = 'Dynamic';
        else if (desc.includes('horizon')) company = 'Horizon';
        else if (desc.includes('lakefront')) company = 'Lakefront';
      }
      
      // If no stored miles, estimate from amount (rough: ~$1.75/mile for driveaway)
      if (miles === 0 && exp.amount) {
        miles = Math.round((exp.amount || 0) / 1.75);
      }
      
      // If no control number in data, try filename
      if (!controlNumber && exp.filename) {
        const bolMatch = exp.filename.match(/(\d{3}-\d{6}-\d{3}|\d{6,})/);
        if (bolMatch) controlNumber = bolMatch[1];
      }
      
      const daysOnRoad = estimateTripDays(miles);
      
      // Estimate start date (trip ends on settlement date, started X days before)
      const endDate = new Date(exp.date);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - daysOnRoad + 1);
      
      return {
        id: exp.id,
        company,
        date: exp.date,
        startDate: startDate.toISOString().split('T')[0],
        origin,
        destination,
        miles,
        amount: exp.amount || 0,
        controlNumber,
        daysOnRoad
      } as Trip;
    }).sort((a, b) => a.date.localeCompare(b.date));
  }, [expenses]);

  // Get trips for the selected month
  const monthTrips = useMemo(() => {
    return trips.filter(trip => {
      const tripDate = new Date(trip.date);
      return tripDate.getFullYear() === selectedYear && 
             tripDate.getMonth() + 1 === selectedMonth;
    });
  }, [trips, selectedYear, selectedMonth]);

  // Build calendar grid with trip markers
  // OTR logic: If trips are within 7 days of each other, count gap days as still "on the road"
  const calendarDays = useMemo(() => {
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0);
    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    // First, build list of all OTR date ranges (including gaps < 7 days between trips)
    const sortedTrips = [...trips].sort((a, b) => (a.startDate || a.date).localeCompare(b.startDate || b.date));
    const otrRanges: { start: string; end: string }[] = [];
    
    for (const trip of sortedTrips) {
      const tripStart = trip.startDate || trip.date;
      const tripEnd = trip.date;
      
      // Check if this trip connects to the last OTR range (within 7 days)
      if (otrRanges.length > 0) {
        const lastRange = otrRanges[otrRanges.length - 1];
        const lastEnd = new Date(lastRange.end);
        const thisStart = new Date(tripStart);
        const daysBetween = Math.floor((thisStart.getTime() - lastEnd.getTime()) / (1000 * 60 * 60 * 24));
        
        // If gap is 7 days or less, extend the range (driver is still OTR)
        if (daysBetween <= 7) {
          lastRange.end = tripEnd > lastRange.end ? tripEnd : lastRange.end;
          continue;
        }
      }
      
      // Start a new OTR range
      otrRanges.push({ start: tripStart, end: tripEnd });
    }
    
    const days: { date: number | null; trips: Trip[]; isOnRoad: boolean }[] = [];
    
    // Padding for start of month
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, trips: [], isOnRoad: false });
    }
    
    // Actual days
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // Find trips that include this day (between startDate and date)
      const dayTrips = trips.filter(trip => {
        const start = trip.startDate || trip.date;
        const end = trip.date;
        return dateStr >= start && dateStr <= end;
      });
      
      // Check if this day falls within any OTR range (includes gap days)
      const isOnRoad = otrRanges.some(range => dateStr >= range.start && dateStr <= range.end);
      
      days.push({
        date: day,
        trips: dayTrips,
        isOnRoad
      });
    }
    
    return days;
  }, [trips, selectedYear, selectedMonth]);

  // Calculate monthly stats
  const monthStats = useMemo(() => {
    const daysOnRoad = calendarDays.filter(d => d.date && d.isOnRoad).length;
    const daysHome = calendarDays.filter(d => d.date && !d.isOnRoad).length;
    const totalIncome = monthTrips.reduce((sum, t) => sum + t.amount, 0);
    const totalMiles = monthTrips.reduce((sum, t) => sum + t.miles, 0);
    
    // Per diem rate varies by year
    const perDiemRate = getPerDiemRate(selectedYear);
    const perDiemTotal = daysOnRoad * perDiemRate;
    
    return { daysOnRoad, daysHome, totalIncome, totalMiles, perDiemTotal, perDiemRate, tripCount: monthTrips.length };
  }, [calendarDays, monthTrips, selectedYear]);

  // Year stats
  const yearStats = useMemo(() => {
    const yearTrips = trips.filter(t => new Date(t.date).getFullYear() === selectedYear);
    const totalIncome = yearTrips.reduce((sum, t) => sum + t.amount, 0);
    const totalMiles = yearTrips.reduce((sum, t) => sum + t.miles, 0);
    
    // Build OTR ranges for the year (same logic as calendar - gaps <= 7 days = still OTR)
    const sortedYearTrips = [...yearTrips].sort((a, b) => (a.startDate || a.date).localeCompare(b.startDate || b.date));
    const otrRanges: { start: string; end: string }[] = [];
    
    for (const trip of sortedYearTrips) {
      const tripStart = trip.startDate || trip.date;
      const tripEnd = trip.date;
      
      if (otrRanges.length > 0) {
        const lastRange = otrRanges[otrRanges.length - 1];
        const lastEnd = new Date(lastRange.end);
        const thisStart = new Date(tripStart);
        const daysBetween = Math.floor((thisStart.getTime() - lastEnd.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysBetween <= 7) {
          lastRange.end = tripEnd > lastRange.end ? tripEnd : lastRange.end;
          continue;
        }
      }
      otrRanges.push({ start: tripStart, end: tripEnd });
    }
    
    // Count days on road for the year using OTR ranges
    let daysOnRoad = 0;
    const yearStart = new Date(selectedYear, 0, 1);
    const yearEnd = new Date(selectedYear, 11, 31);
    
    for (let d = new Date(yearStart); d <= yearEnd; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const isOnRoad = otrRanges.some(range => dateStr >= range.start && dateStr <= range.end);
      if (isOnRoad) daysOnRoad++;
    }
    
    // Per diem rate varies by year
    const perDiemRate = getPerDiemRate(selectedYear);
    const perDiemTotal = daysOnRoad * perDiemRate;
    
    return { totalIncome, totalMiles, daysOnRoad, perDiemTotal, perDiemRate, tripCount: yearTrips.length };
  }, [trips, selectedYear]);

  const getCompanyColor = (company: string): string => {
    for (const [key, color] of Object.entries(COMPANY_COLORS)) {
      if (company.toLowerCase().includes(key.toLowerCase())) {
        return color;
      }
    }
    return 'gray';
  };

  const navigateMonth = (delta: number) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-cyan-400" />
            Trip Calendar
          </h1>
          <p className="text-shuttle-sky mt-1">
            Visualize your trips, locations, and per diem eligibility
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'calendar' 
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            📅 Calendar
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'list' 
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            📋 List
          </button>
        </div>
      </div>

      {/* Year Stats */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl p-6 border border-cyan-500/30">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Truck className="w-5 h-5 text-cyan-400" />
          {selectedYear} Year Overview
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-cyan-400">{yearStats.tripCount}</p>
            <p className="text-white/60 text-sm">Trips</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-400">${yearStats.totalIncome.toLocaleString()}</p>
            <p className="text-white/60 text-sm">Income</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-orange-400">{yearStats.totalMiles.toLocaleString()}</p>
            <p className="text-white/60 text-sm">Miles</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-400">{yearStats.daysOnRoad}</p>
            <p className="text-white/60 text-sm">Days OTR</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-400">${yearStats.perDiemTotal.toLocaleString()}</p>
            <p className="text-white/60 text-sm">Per Diem</p>
          </div>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-4">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
          >
            {MONTHS.map((month, idx) => (
              <option key={month} value={idx + 1}>{month}</option>
            ))}
          </select>
          
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
          >
            {Array.from({ length: 7 }, (_, i) => 2025 - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Month Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <div className="flex items-center gap-2 text-cyan-400 mb-1">
            <Route className="w-4 h-4" />
            <span className="text-xs font-medium">Trips</span>
          </div>
          <p className="text-2xl font-bold text-white">{monthStats.tripCount}</p>
        </div>
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <div className="flex items-center gap-2 text-green-400 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-medium">Income</span>
          </div>
          <p className="text-2xl font-bold text-white">${monthStats.totalIncome.toLocaleString()}</p>
        </div>
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <div className="flex items-center gap-2 text-orange-400 mb-1">
            <Truck className="w-4 h-4" />
            <span className="text-xs font-medium">Miles</span>
          </div>
          <p className="text-2xl font-bold text-white">{monthStats.totalMiles.toLocaleString()}</p>
        </div>
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <div className="flex items-center gap-2 text-purple-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">Days OTR</span>
          </div>
          <p className="text-2xl font-bold text-white">{monthStats.daysOnRoad}</p>
        </div>
        <div className="bg-white/10 rounded-xl p-4 border border-white/20">
          <div className="flex items-center gap-2 text-blue-400 mb-1">
            <Home className="w-4 h-4" />
            <span className="text-xs font-medium">Days Home</span>
          </div>
          <p className="text-2xl font-bold text-white">{monthStats.daysHome}</p>
        </div>
        <div className="bg-emerald-500/20 rounded-xl p-4 border border-emerald-500/30">
          <div className="flex items-center gap-2 text-emerald-400 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-medium">Per Diem</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">${monthStats.perDiemTotal.toLocaleString()}</p>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        /* Calendar View */
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {WEEKDAYS.map(day => (
              <div key={day} className="text-center text-white/60 text-sm font-medium py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-2">
            {calendarDays.map((day, idx) => (
              <div
                key={idx}
                onClick={() => day.trips.length > 0 && setSelectedTrip(day.trips[0])}
                className={`min-h-[80px] rounded-lg p-2 transition-all ${
                  day.date === null 
                    ? 'bg-transparent' 
                    : day.isOnRoad
                      ? 'bg-cyan-500/20 border border-cyan-500/50 cursor-pointer hover:bg-cyan-500/30'
                      : 'bg-white/5 border border-white/10'
                }`}
              >
                {day.date && (
                  <>
                    <div className={`text-sm font-medium ${day.isOnRoad ? 'text-cyan-400' : 'text-white/60'}`}>
                      {day.date}
                    </div>
                    {day.trips.length > 0 && (
                      <div className="mt-1 space-y-1">
                        {day.trips.slice(0, 2).map((trip, i) => (
                          <div
                            key={i}
                            className={`text-xs px-1.5 py-0.5 rounded bg-${getCompanyColor(trip.company)}-500/30 text-${getCompanyColor(trip.company)}-300 truncate`}
                            title={`${trip.company}: ${trip.origin} → ${trip.destination}`}
                          >
                            🚛 {trip.company.substring(0, 8)}
                          </div>
                        ))}
                        {day.trips.length > 2 && (
                          <div className="text-xs text-white/50">+{day.trips.length - 2} more</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-cyan-500/20 border border-cyan-500/50"></div>
              <span className="text-white/70">On the Road (OTR)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-white/5 border border-white/10"></div>
              <span className="text-white/70">Home (7+ day gap)</span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Info className="w-4 h-4 text-white/50" />
              <span className="text-white/50">Gaps ≤7 days between trips = still OTR</span>
            </div>
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="divide-y divide-white/10">
            {monthTrips.length === 0 ? (
              <div className="p-8 text-center text-white/50">
                <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No trips recorded for {MONTHS[selectedMonth - 1]} {selectedYear}</p>
                <p className="text-sm mt-2">Import settlements to see trip data here</p>
              </div>
            ) : (
              monthTrips.map(trip => (
                <div
                  key={trip.id}
                  className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => setSelectedTrip(trip)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium bg-${getCompanyColor(trip.company)}-500/20 text-${getCompanyColor(trip.company)}-400 border border-${getCompanyColor(trip.company)}-500/50`}>
                          {trip.company}
                        </span>
                        <span className="text-white/60 text-sm">{trip.date}</span>
                        {trip.controlNumber && (
                          <span className="text-white/40 text-xs font-mono">#{trip.controlNumber}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-white">
                        <MapPin className="w-4 h-4 text-green-400" />
                        <span className="font-medium">{trip.origin || 'Unknown'}</span>
                        <span className="text-white/40">→</span>
                        <MapPin className="w-4 h-4 text-red-400" />
                        <span className="font-medium">{trip.destination || 'Unknown'}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-2 text-sm text-white/60">
                        <span className="flex items-center gap-1">
                          <Route className="w-3 h-3" />
                          {trip.miles.toLocaleString()} mi
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          ~{trip.daysOnRoad} day{trip.daysOnRoad > 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          Per diem: ${(trip.daysOnRoad * getPerDiemRate(new Date(trip.date).getFullYear())).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-400">
                        ${trip.amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-white/50">
                        ${(trip.amount / Math.max(trip.miles, 1)).toFixed(2)}/mi
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Trip Detail Modal */}
      {selectedTrip && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedTrip(null)}
        >
          <div 
            className="bg-shuttle-dark rounded-2xl p-6 max-w-lg w-full border border-white/20"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Trip Details</h3>
              <button
                onClick={() => setSelectedTrip(null)}
                className="text-white/50 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium bg-${getCompanyColor(selectedTrip.company)}-500/20 text-${getCompanyColor(selectedTrip.company)}-400 border border-${getCompanyColor(selectedTrip.company)}-500/50`}>
                  {selectedTrip.company}
                </span>
                {selectedTrip.controlNumber && (
                  <span className="text-white/60 font-mono">BOL: {selectedTrip.controlNumber}</span>
                )}
              </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-white font-medium">{selectedTrip.origin || 'Unknown Origin'}</span>
                  </div>
                  <span className="text-white/40 text-sm">{selectedTrip.startDate}</span>
                </div>
                <div className="border-l-2 border-dashed border-white/20 ml-1.5 pl-4 py-2 text-white/60 text-sm">
                  {selectedTrip.miles.toLocaleString()} miles • ~{selectedTrip.daysOnRoad} days
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-white font-medium">{selectedTrip.destination || 'Unknown Destination'}</span>
                  </div>
                  <span className="text-white/40 text-sm">{selectedTrip.date}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                  <p className="text-green-400 text-sm mb-1">Settlement</p>
                  <p className="text-2xl font-bold text-white">${selectedTrip.amount.toLocaleString()}</p>
                </div>
                <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30">
                  <p className="text-emerald-400 text-sm mb-1">Per Diem Eligible</p>
                  <p className="text-2xl font-bold text-white">${(selectedTrip.daysOnRoad * getPerDiemRate(new Date(selectedTrip.date).getFullYear())).toLocaleString()}</p>
                </div>
              </div>
              
              <div className="text-sm text-white/50 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Per diem: ${getPerDiemRate(new Date(selectedTrip.date).getFullYear())}/day ({new Date(selectedTrip.date).getFullYear()} rate). Truckers can deduct 80% = ${(getPerDiemRate(new Date(selectedTrip.date).getFullYear()) * 0.8).toFixed(2)}/day.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">📍 About Trip Calendar & Per Diem</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-white/70">
          <div>
            <h4 className="font-medium text-cyan-400 mb-2">Data Sources:</h4>
            <ul className="space-y-1">
              <li>• Settlement imports (origin/destination from BOLs)</li>
              <li>• Bank deposit records (company identification)</li>
              <li>• Miles estimated from settlement amounts (~$1.75/mi)</li>
              <li>• Trip days estimated at ~500 miles/day</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-emerald-400 mb-2">Per Diem Rates (M&IE):</h4>
            <ul className="space-y-1">
              <li>• <strong>2025:</strong> $68/day → 80% = <strong>$54.40</strong> deductible</li>
              <li>• <strong>2024:</strong> $59/day → 80% = $47.20 deductible</li>
              <li>• <strong>2020-2021:</strong> $66/day → 80% = $52.80 deductible</li>
              <li>• Must be away from tax home overnight</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

